"use client";
import React, { useCallback, useMemo } from "react";

import { useTranslations } from "next-intl";

// Import icons
import UploadFileIcon from "@/icons/icon_upload_file.svg";
import CloseIcon from "@/icons/ui/buttons/close.svg";
import FormWrapper from "@/ui/Forms/FormWrapper";
import { cn } from "@/utils/cn";

export interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  url?: string;
  file?: File;
}

export interface FormUploadProps {
  name: string;
  label?: string;
  value?: UploadedFile | UploadedFile[] | null;
  onChange?: (files: UploadedFile[] | null) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  accept?: string;
  maxFiles?: number;
}

/**
 * FormUpload component
 * File upload component with drag and drop support
 */
const FormUpload: React.FC<FormUploadProps> = ({
  name,
  label,
  value = null,
  onChange,
  error,
  required = false,
  disabled = false,
  className = "",
  accept = "application/pdf,image/png,image/jpeg,image/jpg",
  maxFiles = 10,
}) => {
  const t = useTranslations();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = React.useState<boolean>(false);
  const [errorState, setErrorState] = React.useState<{
    title: string;
    subtitle: string;
  } | null>(null);

  // Memoize allowedTypes to use in handlers
  const allowedTypes = useMemo(
    () => ["application/pdf", "image/png", "image/jpeg", "image/jpg"],
    [],
  );

  // Convert value to array for unified handling with memoization
  const files = useMemo(() => {
    return value ? (Array.isArray(value) ? value : [value]) : [];
  }, [value]);

  // Memoize function for creating new file
  const createFileObject = useCallback((file: File): UploadedFile => {
    return {
      id: Math.random().toString(36).substring(2, 15),
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
      url: URL.createObjectURL(file),
    };
  }, []);

  // Memoize function for file validation and processing
  const processFiles = useCallback(
    (selectedFiles: FileList | null) => {
      if (!selectedFiles || !onChange) {
        return;
      }

      const newFiles: UploadedFile[] = [];
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        if (!allowedTypes.includes(file.type)) {
          setErrorState({
            title: t("upload.file.uploaded"),
            subtitle: t(t("upload.file.error")),
          });
          continue;
        }
        newFiles.push(createFileObject(file));
      }

      setErrorState(null);
      // Add to existing files, limit to maxFiles
      const merged = [...files, ...newFiles].slice(0, maxFiles);
      onChange(merged.length > 0 ? merged : null);
    },
    [allowedTypes, createFileObject, files, maxFiles, onChange, t],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      processFiles(e.target.files);
      e.target.value = ""; // Reset input
    },
    [processFiles],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragging(true);
      }
    },
    [disabled],
  );

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (disabled) {
        return;
      }
      processFiles(e.dataTransfer.files);
    },
    [disabled, processFiles],
  );

  const handleRemoveFile = useCallback(
    (fileId: string) => {
      if (!onChange) {
        return;
      }
      const filtered = files.filter((f) => f.id !== fileId);
      // Revoke blob URLs
      const removed = files.find((f) => f.id === fileId);
      if (removed?.url?.startsWith("blob:")) {
        URL.revokeObjectURL(removed.url);
      }
      onChange(filtered.length > 0 ? filtered : null);
    },
    [onChange, files],
  );

  const handleButtonClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  // Memoize dropzone active state
  const dropzoneClasses = useMemo(() => {
    return cn(
      "form-upload-dropzone",
      files.length > 0 && "form-upload-dropzone-active",
      isDragging && "form-upload-dragging",
      disabled && "form-base-disabled",
    );
  }, [files.length, isDragging, disabled]);

  // Memoize icon state
  const iconClasses = useMemo(() => {
    return cn(
      "form-upload-icon",
      files.length > 0 && "form-upload-icon-active",
    );
  }, [files.length]);

  return (
    <div className={cn("form-upload-base", className)}>
      <FormWrapper
        label={label}
        error={error}
        required={required}
      >
        <div className="form-upload-container">
          <div
            onClick={handleButtonClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={dropzoneClasses}
          >
            <input
              ref={fileInputRef}
              type="file"
              name={name}
              accept={accept}
              onChange={handleFileChange}
              disabled={disabled}
              className="hidden"
              multiple
            />
            <UploadFileIcon className={iconClasses} />
          </div>

          {/* Render file list */}
          {files.length > 0 && (
            <div className="form-upload-file-list">
              {files.map((file) => (
                <div
                  key={file.id}
                  className="form-upload-file-info"
                >
                  <span className="form-upload-filename">{file.name}</span>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(file.id)}
                      className="form-upload-remove-button"
                      aria-label="Remove file"
                    >
                      <CloseIcon className="form-upload-remove-icon" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {errorState && (
            <div className="form-upload-error-container">
              <p className="form-upload-error-text">{errorState.title}</p>
              <p className="form-upload-error-text">{errorState.subtitle}</p>
            </div>
          )}
        </div>
      </FormWrapper>
    </div>
  );
};

export default React.memo(FormUpload);
